/**
 * Yandex.Metrika utility with consent-based initialization.
 */

const COUNTER_ID = import.meta.env.VITE_YANDEX_METRIKA_COUNTER_ID || '90123456';

let metrikaInitialized = false;

/**
 * Get current consent from localStorage.
 */
export function getConsent() {
  const consentJson = localStorage.getItem('consent_v1');
  if (!consentJson) return null;

  try {
    return JSON.parse(consentJson);
  } catch {
    return null;
  }
}

/**
 * Dynamically load and initialize Yandex.Metrika script.
 * Only initializes if analytics consent is given.
 */
export function initYandexMetrika() {
  if (metrikaInitialized) return;

  const consent = getConsent();

  // Don't initialize if no consent or analytics not allowed
  if (!consent?.categories?.analytics) {
    return;
  }

  // Create and load script dynamically using official Yandex Metrika CDN
  const script = document.createElement('script');
  script.async = true;
  script.type = 'text/javascript';

  // Use official Yandex Metrika script with inline initialization
  script.innerHTML = `
    (function(m, e, t, r, i, k, a) {
      m[i] = m[i] || function() { (m[i].a = m[i].a || []).push(arguments) }
      m[i].l = 1 * new Date()
      for (var j = 0; j < document.scripts.length; j++) {
        if (document.scripts[j].src === r) { return; }
      }
      k = e.createElement(t)
      a = e.getElementsByTagName(t)[0]
      k.async = 1
      k.src = r
      a.parentNode.insertBefore(k, a)
    })(window, document, 'script', 'https://mc.yandex.ru/metrika/tag.js', 'ym')

    ym(${COUNTER_ID}, 'init', {
      accurateTrackBounce: true,
      clickmap: true,
      trackLinks: true,
      webvisor: true,
    })
  `;

  script.onload = () => {
    // Check if window.ym is properly initialized
    if (typeof (window as any).ym === 'function') {
      metrikaInitialized = true;
      flushYandexEvents();
    } else {
      console.warn('[Yandex.Metrika] Failed to initialize - window.ym is not a function');
    }
  };

  script.onerror = () => {
    console.warn('[Yandex.Metrika] Failed to load analytics script');
  };

  document.head.appendChild(script);
}

/**
 * Send goal to Yandex.Metrika or buffer if not initialized.
 */
export function trackEvent(goalName: string, params?: Record<string, any>) {
  const consent = getConsent();

  if (consent?.categories?.analytics && metrikaInitialized) {
    // Send immediately if initialized and consented
    try {
      if (typeof (window as any).ym === 'function') {
        (window as any).ym(COUNTER_ID, 'reachGoal', goalName, params);
      } else {
        bufferEvent({ name: goalName, params });
      }
    } catch (e) {
      console.error('[Yandex.Metrika] Error tracking event:', e);
      bufferEvent({ name: goalName, params });
    }
  } else {
    // Buffer event for later if not ready yet
    bufferEvent({ name: goalName, params });
  }
}

/**
 * Buffer event in sessionStorage for sending later.
 */
function bufferEvent(event: { name: string; params?: Record<string, any> }) {
  try {
    const buffer = JSON.parse(sessionStorage.getItem('yandex_events_buffer') || '[]');
    buffer.push(event);
    sessionStorage.setItem('yandex_events_buffer', JSON.stringify(buffer));
  } catch (e) {
    console.error('[Yandex.Metrika] Failed to buffer event:', e);
  }
}

/**
 * Send all buffered events to Yandex.Metrika.
 */
export function flushYandexEvents() {
  if (!metrikaInitialized) return;

  try {
    if (typeof (window as any).ym !== 'function') {
      console.warn('[Yandex.Metrika] window.ym is not available, keeping buffer');
      return;
    }

    const buffer = JSON.parse(sessionStorage.getItem('yandex_events_buffer') || '[]');

    buffer.forEach((event: any) => {
      try {
        (window as any).ym(COUNTER_ID, 'reachGoal', event.name, event.params);
      } catch (e) {
        console.error('[Yandex.Metrika] Error flushing event:', e);
      }
    });

    sessionStorage.removeItem('yandex_events_buffer');
  } catch (e) {
    console.error('[Yandex.Metrika] Failed to flush events:', e);
  }
}

/**
 * Clear all consent and re-show banner.
 */
export function resetConsent() {
  localStorage.removeItem('consent_v1');
  metrikaInitialized = false;
}
